import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useStore } from '../../context/StoreContext';
import { TRANSLATIONS } from '../../constants';
import { 
  Package, Folder, ShoppingCart, Trash2, Plus, Edit, Save, X, 
  Search, TrendingUp, DollarSign, BarChart3, AlertCircle, Check, Clock,
  Truck, XCircle, Eye, Image as ImageIcon, Upload, LogOut, User, ExternalLink,
  Loader2
} from 'lucide-react';
import { Category, Product, Order, Media } from '../../types';
import { mediaApi, productsApi, categoriesApi } from '../../services/apiService';
import { Pagination } from '../../components/Pagination';

const ADMIN_PAGE_SIZE = 10;

type TabType = 'analytics' | 'products' | 'orders' | 'categories' | 'library';

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { 
    products, orders, categories, 
    addProduct, deleteProduct, updateProduct,
    updateCategory, addCategory, deleteCategory,
    updateOrderStatus, deleteOrder, language, refreshOrders 
  } = useStore();
  
  // Get tab from URL or default to analytics
  const tabFromUrl = (searchParams.get('tab') as TabType) || 'analytics';
  const [activeTab, setActiveTab] = useState<TabType>(tabFromUrl);
  const [adminUser, setAdminUser] = useState('');
  const t = TRANSLATIONS;

  // Update URL when tab changes
  useEffect(() => {
    setSearchParams({ tab: activeTab });
  }, [activeTab, setSearchParams]);

  // Sync tab with URL on mount and URL changes
  useEffect(() => {
    const urlTab = searchParams.get('tab') as TabType;
    if (urlTab && urlTab !== activeTab) {
      setActiveTab(urlTab);
    }
  }, [searchParams]);

  // Search and Filter States
  const [productSearch, setProductSearch] = useState('');
  const [orderSearch, setOrderSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  // Server-side Pagination States for Products
  const [adminProducts, setAdminProducts] = useState<Product[]>([]);
  const [productPage, setProductPage] = useState(1);
  const [productTotalPages, setProductTotalPages] = useState(1);
  const [productTotal, setProductTotal] = useState(0);
  const [productsLoading, setProductsLoading] = useState(false);

  // Server-side Pagination States for Categories
  const [adminCategories, setAdminCategories] = useState<Category[]>([]);
  const [categoryPage, setCategoryPage] = useState(1);
  const [categoryTotalPages, setCategoryTotalPages] = useState(1);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  // Product Form State
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [newProduct, setNewProduct] = useState<Partial<Product>>({
    name: '',
    price: 0,
    categoryId: categories[0]?.id || '',
    description: '',
    adminNote: '',
    image: 'https://picsum.photos/400/400',
    stock: 0,
    discount: 0,
    featured: false,
    minQuantity: 6,
  });
  
  // Category Edit State
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [newCategory, setNewCategory] = useState({ id: '', nameEn: '', namePl: '' });
  const [showAddCategory, setShowAddCategory] = useState(false);

  // Media Library State
  const [mediaLibrary, setMediaLibrary] = useState<Media[]>([]);
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [newMediaUrl, setNewMediaUrl] = useState('');
  const [mediaSearch, setMediaSearch] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Order Details
  const [showOrderDetails, setShowOrderDetails] = useState<string | null>(null);

  // Refresh orders when admin dashboard mounts (only once)
  useEffect(() => {
    refreshOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load media library
  useEffect(() => {
    const loadMedia = async () => {
      try {
        const response = await mediaApi.getAll();
        if (response?.data) {
          setMediaLibrary(response.data);
        }
      } catch (error) {
        console.error('Failed to load media library:', error);
      }
    };
    loadMedia();
  }, []);

  // Handle media upload
  const handleAddMedia = async () => {
    if (!newMediaUrl.trim()) return;
    
    try {
      const filename = newMediaUrl.split('/').pop() || 'image';
      const response = await mediaApi.create({
        filename,
        url: newMediaUrl,
      });
      
      if (response?.data) {
        setMediaLibrary(prev => [response.data, ...prev]);
        setNewMediaUrl('');
      }
    } catch (error) {
      console.error('Failed to add media:', error);
      alert('Failed to add image to library');
    }
  };

  // Handle media deletion
  const handleDeleteMedia = async (id: string) => {
    if (!confirm(language === 'en' ? 'Delete this image?' : 'Usunąć ten obraz?')) return;
    
    try {
      await mediaApi.delete(id);
      setMediaLibrary(prev => prev.filter(m => m.id !== id));
    } catch (error) {
      console.error('Failed to delete media:', error);
      alert('Failed to delete image');
    }
  };

  // Handle file upload (single file)
  const handleFileUpload = async (file: File) => {
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert(language === 'en' 
        ? 'Invalid file type. Please upload JPEG, PNG, GIF, or WebP images.' 
        : 'Nieprawidłowy typ pliku. Proszę przesłać obrazy JPEG, PNG, GIF lub WebP.');
      return;
    }
    
    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert(language === 'en' 
        ? 'File too large. Maximum size is 5MB.' 
        : 'Plik jest za duży. Maksymalny rozmiar to 5MB.');
      return;
    }
    
    setUploadingFile(true);
    
    try {
      const response = await mediaApi.upload(file);
      
      if (response?.data) {
        setMediaLibrary(prev => [response.data, ...prev]);
        alert(language === 'en' ? 'Image uploaded successfully!' : 'Obraz został przesłany!');
      }
    } catch (error) {
      console.error('Failed to upload file:', error);
      alert(language === 'en' 
        ? 'Failed to upload image. Please try again.' 
        : 'Nie udało się przesłać obrazu. Spróbuj ponownie.');
    } finally {
      setUploadingFile(false);
    }
  };

  // Handle multiple file uploads
  const handleMultipleFileUpload = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    
    if (fileArray.length === 0) return;
    
    // Validate file count limit
    if (fileArray.length > 200) {
      alert(language === 'en' 
        ? `Too many files. Maximum 200 files can be uploaded at once. You selected ${fileArray.length} files.` 
        : `Za dużo plików. Maksymalnie 200 plików można przesłać na raz. Wybrano ${fileArray.length} plików.`);
      return;
    }
    
    // Validate file types and sizes
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const invalidFiles = fileArray.filter(file => !allowedTypes.includes(file.type));
    const oversizedFiles = fileArray.filter(file => file.size > 5 * 1024 * 1024);
    
    if (invalidFiles.length > 0) {
      alert(language === 'en' 
        ? `${invalidFiles.length} file(s) have invalid type. Only JPEG, PNG, GIF, and WebP are allowed.` 
        : `${invalidFiles.length} plik(ów) ma nieprawidłowy typ. Dozwolone są tylko JPEG, PNG, GIF i WebP.`);
      return;
    }
    
    if (oversizedFiles.length > 0) {
      alert(language === 'en' 
        ? `${oversizedFiles.length} file(s) exceed 5MB limit.` 
        : `${oversizedFiles.length} plik(ów) przekracza limit 5MB.`);
      return;
    }
    
    setUploadingFile(true);
    
    try {
      const response = await mediaApi.uploadMultiple(fileArray);
      
      if (response?.data && Array.isArray(response.data)) {
        setMediaLibrary(prev => [...response.data, ...prev]);
        alert(language === 'en' 
          ? `${response.data.length} image(s) uploaded successfully!` 
          : `${response.data.length} obraz(ów) został(o) przesłany(ch)!`);
      }
    } catch (error) {
      console.error('Failed to upload files:', error);
      alert(language === 'en' 
        ? 'Failed to upload images. Please try again.' 
        : 'Nie udało się przesłać obrazów. Spróbuj ponownie.');
    } finally {
      setUploadingFile(false);
    }
  };

  // Handle file input change (supports multiple files)
  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      if (files.length === 1) {
        handleFileUpload(files[0]);
      } else {
        handleMultipleFileUpload(files);
      }
    }
    // Reset input
    e.target.value = '';
  };

  // Handle drag and drop (supports multiple files)
  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      if (files.length === 1) {
        handleFileUpload(files[0]);
      } else {
        handleMultipleFileUpload(files);
      }
    }
  };

  // Select image from library
  const selectImageFromLibrary = (url: string) => {
    if (editingProduct) {
      setEditingProduct({ ...editingProduct, image: url });
    } else {
      setNewProduct({ ...newProduct, image: url });
    }
    setShowImagePicker(false);
  };

  // Analytics Calculations
  const analytics = useMemo(() => {
    const totalSales = orders.reduce((sum, order) => sum + (Number(order.total) || 0), 0);
    const totalOrdersCount = orders.length;
    const avgOrderValue = totalOrdersCount > 0 ? totalSales / totalOrdersCount : 0;
    
    const ordersByStatus = {
      pending: orders.filter(o => o.status === 'pending').length,
      processing: orders.filter(o => o.status === 'processing').length,
      shipped: orders.filter(o => o.status === 'shipped').length,
      delivered: orders.filter(o => o.status === 'delivered').length,
      cancelled: orders.filter(o => o.status === 'cancelled').length,
    };

    // Calculate top products
    const productSales: Record<string, { product: Product; quantity: number; revenue: number }> = {};
    orders.forEach(order => {
      order.items.forEach(item => {
        const product = products.find(p => p.id === item.id);
        if (product) {
          if (!productSales[item.id]) {
            productSales[item.id] = { product, quantity: 0, revenue: 0 };
          }
          productSales[item.id].quantity += item.quantity;
          productSales[item.id].revenue += item.price * item.quantity;
        }
      });
    });

    const topProducts = Object.values(productSales)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);

    const lowStockProducts = products.filter(p => (p.stock || 0) < 10 && (p.stock || 0) > 0);
    const outOfStockProducts = products.filter(p => (p.stock || 0) === 0);

    return {
      totalSales,
      totalOrders: totalOrdersCount,
      avgOrderValue,
      ordersByStatus,
      topProducts,
      lowStockProducts,
      outOfStockProducts,
    };
  }, [orders, products]);

  // Fetch paginated products from API
  const fetchAdminProducts = useCallback(async () => {
    setProductsLoading(true);
    try {
      const params: any = { page: productPage, limit: ADMIN_PAGE_SIZE };
      if (filterCategory !== 'all') params.category = filterCategory;
      if (productSearch.trim()) params.search = productSearch.trim();

      const response = await productsApi.getAll(params);
      if (response.success && response.data) {
        const transformedProducts = (response.data as any[]).map((p: any) => ({
          id: p.id,
          name: p.name,
          slug: p.slug,
          price: typeof p.price === 'string' ? parseFloat(p.price) : p.price,
          categoryId: p.category_id || p.categoryId,
          description: p.description,
          image: p.image,
          stock: p.stock,
          discount: p.discount || 0,
          featured: p.featured || false,
          adminNote: p.admin_note ?? p.adminNote ?? null,
          minQuantity: p.min_quantity || p.minQuantity || 6,
          createdAt: p.created_at || p.createdAt,
          updatedAt: p.updated_at || p.updatedAt
        }));
        setAdminProducts(transformedProducts);
        setProductTotal((response as any).total || transformedProducts.length);
        setProductTotalPages((response as any).totalPages || 1);
      }
    } catch (error) {
      console.error('Error fetching admin products:', error);
    } finally {
      setProductsLoading(false);
    }
  }, [productPage, filterCategory, productSearch]);

  // Fetch paginated categories from API
  const fetchAdminCategories = useCallback(async () => {
    setCategoriesLoading(true);
    try {
      const response = await categoriesApi.getAll();
      if (response.success && response.data) {
        const allCategories = (response.data as any[]).map((c: any) => ({
          id: c.id,
          name: {
            en: c.name_en || c.name?.en || '',
            pl: c.name_pl || c.name?.pl || ''
          },
          slug: c.slug
        }));
        // Client-side pagination for categories (small dataset)
        const start = (categoryPage - 1) * ADMIN_PAGE_SIZE;
        setAdminCategories(allCategories.slice(start, start + ADMIN_PAGE_SIZE));
        setCategoryTotalPages(Math.ceil(allCategories.length / ADMIN_PAGE_SIZE));
      }
    } catch (error) {
      console.error('Error fetching admin categories:', error);
    } finally {
      setCategoriesLoading(false);
    }
  }, [categoryPage]);

  // Fetch products when page/filter changes
  useEffect(() => {
    fetchAdminProducts();
  }, [fetchAdminProducts]);

  // Reset page when filters change
  useEffect(() => {
    setProductPage(1);
  }, [productSearch, filterCategory]);

  // Fetch categories when page changes
  useEffect(() => {
    fetchAdminCategories();
  }, [fetchAdminCategories]);

  // Filtered Orders
  const filteredOrders = useMemo(() => {
    return orders.filter(o => {
      const matchesSearch = o.id.toLowerCase().includes(orderSearch.toLowerCase()) ||
                           (o.customer?.name || '').toLowerCase().includes(orderSearch.toLowerCase()) ||
                           (o.customer?.email || '').toLowerCase().includes(orderSearch.toLowerCase());
      const matchesStatus = filterStatus === 'all' || o.status === filterStatus;
      return matchesSearch && matchesStatus;
    });
  }, [orders, orderSearch, filterStatus]);

  const handleAddProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newProduct.name || !newProduct.price) return;

    const product: Product = {
      id: Math.random().toString(36).substr(2, 9),
      name: newProduct.name,
      price: newProduct.price,
      categoryId: newProduct.categoryId || categories[0]?.id || '',
      description: newProduct.description || '',
      image: newProduct.image || 'https://picsum.photos/400/400',
      stock: newProduct.stock || 0,
      discount: newProduct.discount || 0,
      featured: newProduct.featured || false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    await addProduct(product);
    setNewProduct({
      name: '',
      price: 0,
      categoryId: categories[0]?.id || '',
      description: '',
      adminNote: '',
      image: 'https://picsum.photos/400/400',
      stock: 0,
      discount: 0,
      featured: false,
      minQuantity: 6,
    });
    setShowProductModal(false);
    fetchAdminProducts();
  };

  const handleUpdateProduct = async (e: React.FormEvent) => {
    e.preventDefault();
    if (editingProduct) {
      await updateProduct({ ...editingProduct, updatedAt: new Date().toISOString() });
      setEditingProduct(null);
      setShowProductModal(false);
      fetchAdminProducts();
    }
  };

  const openAddProductModal = () => {
    setEditingProduct(null);
    setNewProduct({
      name: '',
      price: 0,
      categoryId: categories[0]?.id || '',
      description: '',
      adminNote: '',
      image: 'https://picsum.photos/400/400',
      stock: 0,
      discount: 0,
      featured: false,
      minQuantity: 6,
    });
    setShowProductModal(true);
  };

  const openEditProductModal = (product: Product) => {
    setEditingProduct(product);
    setShowProductModal(true);
  };

  const closeProductModal = () => {
    setShowProductModal(false);
    setEditingProduct(null);
  };

  const handleUpdateCategory = async () => {
    if (editingCategory) {
      await updateCategory(editingCategory);
      setEditingCategory(null);
      fetchAdminCategories();
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.id || !newCategory.nameEn || !newCategory.namePl) return;
    
    const category: Category = {
      id: newCategory.id,
      name: { en: newCategory.nameEn, pl: newCategory.namePl }
    };
    
    await addCategory(category);
    setNewCategory({ id: '', nameEn: '', namePl: '' });
    setShowAddCategory(false);
    fetchAdminCategories();
  };

  const handleDeleteCategory = async (id: string) => {
    const hasProducts = products.some(p => p.categoryId === id);
    if (hasProducts) {
      alert('Cannot delete category with products. Please reassign or delete products first.');
      return;
    }
    if (confirm(t.confirmDelete[language])) {
      await deleteCategory(id);
      fetchAdminCategories();
    }
  };

  const getStatusColor = (status: Order['status']) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'processing': return 'bg-blue-100 text-blue-800';
      case 'shipped': return 'bg-purple-100 text-purple-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
    }
  };

  const getStockStatus = (stock: number) => {
    if (stock === 0) return { label: t.outOfStock[language], color: 'text-red-600' };
    if (stock < 10) return { label: t.lowStock[language], color: 'text-orange-600' };
    return { label: t.inStock[language], color: 'text-green-600' };
  };

  // Get admin username
  useEffect(() => {
    try {
      const userStr = localStorage.getItem('adminUser');
      if (userStr) {
        const user = JSON.parse(userStr);
        setAdminUser(user.username || 'Admin');
      } else {
        setAdminUser('Admin');
      }
    } catch (error) {
      console.error('Error parsing admin user:', error);
      setAdminUser('Admin');
    }
  }, []);

  // Logout function
  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-3xl font-bold text-slate-800">{t.adminDashboard[language]}</h1>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-gray-600">
            <User className="w-5 h-5" />
            <span className="font-medium">{adminUser}</span>
          </div>
          <button
            onClick={() => {
              throw new Error('Sentry frontend test error from admin dashboard');
            }}
            className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
            title="Trigger a test error for Sentry"
          >
            Test Sentry
          </button>
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            {language === 'en' ? 'Logout' : 'Wyloguj'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar */}
        <div className="lg:col-span-1 flex flex-row lg:flex-col overflow-x-auto pb-4 lg:pb-0 gap-2">
          <button
            onClick={() => setActiveTab('analytics')}
            className={`flex-shrink-0 lg:w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'analytics' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <TrendingUp className="w-5 h-5" />
            <span className="whitespace-nowrap">{t.analytics[language]}</span>
          </button>
          <button
            onClick={() => setActiveTab('products')}
            className={`flex-shrink-0 lg:w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'products' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Package className="w-5 h-5" />
            <span className="whitespace-nowrap">{t.products[language]}</span>
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`flex-shrink-0 lg:w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'orders' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <ShoppingCart className="w-5 h-5" />
            <span className="whitespace-nowrap">{t.manageOrders[language]}</span>
          </button>
          <button
            onClick={() => setActiveTab('categories')}
            className={`flex-shrink-0 lg:w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'categories' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Folder className="w-5 h-5" />
            <span className="whitespace-nowrap">{t.manageCategories[language]}</span>
          </button>
          <button
            onClick={() => setActiveTab('library')}
            className={`flex-shrink-0 lg:w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-colors ${
              activeTab === 'library' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            <ImageIcon className="w-5 h-5" />
            <span className="whitespace-nowrap">{language === 'en' ? 'Image Library' : 'Biblioteka Obrazów'}</span>
          </button>
        </div>

        {/* Content Area */}
        <div className="lg:col-span-3">
          {/* ANALYTICS TAB */}
          {activeTab === 'analytics' && (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">{t.totalSales[language]}</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">
                        {(Number(analytics.totalSales) || 0).toFixed(2)} zł
                      </p>
                    </div>
                    <div className="bg-green-100 p-3 rounded-lg">
                      <DollarSign className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">{t.totalOrders[language]}</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">{analytics.totalOrders}</p>
                    </div>
                    <div className="bg-blue-100 p-3 rounded-lg">
                      <ShoppingCart className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-600">{t.avgOrderValue[language]}</p>
                      <p className="text-2xl font-bold text-gray-900 mt-1">
                        {(Number(analytics.avgOrderValue) || 0).toFixed(2)} zł
                      </p>
                    </div>
                    <div className="bg-purple-100 p-3 rounded-lg">
                      <TrendingUp className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Status Overview */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <h3 className="text-lg font-bold text-gray-800 mb-4">{t.status[language]} {t.manageOrders[language]}</h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {Object.entries(analytics.ordersByStatus).map(([status, count]) => (
                    <div key={status} className="text-center">
                      <p className="text-2xl font-bold text-gray-900">{count}</p>
                      <p className="text-sm text-gray-600 capitalize">{t[status as keyof typeof t]?.[language] || status}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Top Products */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <h3 className="text-lg font-bold text-gray-800 mb-4">{t.topProducts[language]}</h3>
                {analytics.topProducts.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No sales data yet</p>
                ) : (
                  <div className="space-y-3">
                    {analytics.topProducts.map(({ product, quantity, revenue }, index) => (
                      <div key={product.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-gray-400">#{index + 1}</span>
                          <div>
                            <p className="font-medium text-gray-900">{product.name}</p>
                            <p className="text-sm text-gray-500">{quantity} sold</p>
                          </div>
                        </div>
                        <p className="font-bold text-indigo-600">{(Number(revenue) || 0).toFixed(2)} zł</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Stock Alerts */}
              {(analytics.lowStockProducts.length > 0 || analytics.outOfStockProducts.length > 0) && (
                <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-orange-500" />
                    {t.stock[language]} Alerts
                  </h3>
                  <div className="space-y-2">
                    {analytics.outOfStockProducts.map(p => (
                      <div key={p.id} className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                        <span className="text-gray-900">{p.name}</span>
                        <span className="text-red-600 font-medium">{t.outOfStock[language]}</span>
                      </div>
                    ))}
                    {analytics.lowStockProducts.map(p => (
                      <div key={p.id} className="flex justify-between items-center p-3 bg-orange-50 rounded-lg">
                        <span className="text-gray-900">{p.name}</span>
                        <span className="text-orange-600 font-medium">{p.stock} left</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* PRODUCTS TAB */}
          {activeTab === 'products' && (
            <div className="space-y-8">
              {/* Add Product Button */}
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <h2 className="text-xl font-bold text-gray-800">{t.manageProducts[language]}</h2>
                  {!productsLoading && productTotal > 0 && (
                    <span className="text-sm text-gray-500">
                      {productTotal} {language === 'en' ? 'products' : 'produktów'}
                    </span>
                  )}
                </div>
                <button
                  onClick={openAddProductModal}
                  className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-6 py-3 rounded-lg transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  {t.addProduct[language]}
                </button>
              </div>

              {/* Search and Filter */}
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder={t.search[language]}
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                    />
                  </div>
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                  >
                    <option value="all">{t.all[language]} {t.categories[language]}</option>
                    {categories.map(c => (
                      <option key={c.id} value={c.id}>{c.name[language]}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Product List */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {productsLoading && (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                    <span className="ml-2 text-gray-600">Loading products...</span>
                  </div>
                )}
                {!productsLoading && (
                <div className="overflow-x-auto">
                  <table className="w-full text-left min-w-[800px]">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-4 font-medium text-gray-500 text-sm">Product</th>
                        <th className="px-6 py-4 font-medium text-gray-500 text-sm">{t.price[language]}</th>
                        <th className="px-6 py-4 font-medium text-gray-500 text-sm">{t.stock[language]}</th>
                        <th className="px-6 py-4 font-medium text-gray-500 text-sm">{t.status[language]}</th>
                        <th className="px-6 py-4 font-medium text-gray-500 text-sm">{t.actions[language]}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {adminProducts.map(p => {
                        const stockStatus = getStockStatus(p.stock || 0);
                        const finalPrice = p.discount ? p.price * (1 - p.discount / 100) : p.price;
                        return (
                          <tr key={p.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <img src={p.image} alt={p.name} className="w-12 h-12 object-cover rounded" />
                                <div>
                                  <div className="font-medium text-gray-900">{p.name}</div>
                                  {p.featured && (
                                    <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-0.5 rounded">
                                      {t.isFeatured[language]}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-gray-900">
                                {finalPrice.toFixed(2)} zł
                                {p.discount && p.discount > 0 && (
                                  <div className="text-xs text-red-600">-{p.discount}%</div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 text-gray-600">{p.stock || 0}</td>
                            <td className="px-6 py-4">
                              <span className={`text-sm font-medium ${stockStatus.color}`}>
                                {stockStatus.label}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex gap-2">
                                {p.slug && (
                                  <a
                                    href={`/product/${p.slug}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-green-600 hover:text-green-800"
                                    title={language === 'en' ? 'View product page' : 'Zobacz stronę produktu'}
                                  >
                                    <ExternalLink className="w-5 h-5" />
                                  </a>
                                )}
                                <button 
                                  onClick={() => openEditProductModal(p)} 
                                  className="text-indigo-600 hover:text-indigo-800"
                                  title={t.edit[language]}
                                >
                                  <Edit className="w-5 h-5" />
                                </button>
                                <button 
                                  onClick={async () => {
                                    if (confirm(t.confirmDelete[language])) {
                                      await deleteProduct(p.id);
                                      fetchAdminProducts();
                                    }
                                  }} 
                                  className="text-red-400 hover:text-red-600"
                                  title={t.delete[language]}
                                >
                                  <Trash2 className="w-5 h-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {adminProducts.length === 0 && (
                    <div className="p-8 text-center text-gray-500">
                      No products found
                    </div>
                  )}
                </div>
                )}
              </div>

              {/* Product Pagination */}
              <Pagination
                currentPage={productPage}
                totalPages={productTotalPages}
                onPageChange={setProductPage}
              />
            </div>
          )}

          {/* ORDERS TAB */}
          {activeTab === 'orders' && (
            <div className="space-y-6">
              {/* Search and Filter */}
              <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder={`${t.search[language]} orders...`}
                      value={orderSearch}
                      onChange={(e) => setOrderSearch(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                    />
                  </div>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                  >
                    <option value="all">{t.all[language]} {t.status[language]}</option>
                    <option value="pending">{t.pending[language]}</option>
                    <option value="processing">{t.processing[language]}</option>
                    <option value="shipped">{t.shipped[language]}</option>
                    <option value="delivered">{t.delivered[language]}</option>
                    <option value="cancelled">{t.cancelled[language]}</option>
                  </select>
                </div>
              </div>

              {/* Orders List */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                {filteredOrders.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">No orders found.</div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {filteredOrders.map(order => (
                      <div key={order.id} className="p-6">
                        <div className="flex flex-col sm:flex-row justify-between items-start mb-4 gap-4">
                          <div>
                            <h3 className="font-bold text-gray-900">Order #{order.id}</h3>
                            <p className="text-sm text-gray-500">{new Date(order.date).toLocaleString()}</p>
                          </div>
                          <div className="flex items-center gap-3">
                            <select
                              value={order.status}
                              onChange={(e) => updateOrderStatus(order.id, e.target.value as Order['status'])}
                              className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(order.status)} border-0 cursor-pointer`}
                            >
                              <option value="pending">{t.pending[language]}</option>
                              <option value="processing">{t.processing[language]}</option>
                              <option value="shipped">{t.shipped[language]}</option>
                              <option value="delivered">{t.delivered[language]}</option>
                              <option value="cancelled">{t.cancelled[language]}</option>
                            </select>
                            <button
                              onClick={() => setShowOrderDetails(showOrderDetails === order.id ? null : order.id)}
                              className="text-indigo-600 hover:text-indigo-800"
                              title="View Details"
                            >
                              <Eye className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => {
                                if (confirm(language === 'en' ? `Delete order #${order.id}?` : `Usunąć zamówienie #${order.id}?`)) {
                                  deleteOrder(order.id);
                                }
                              }}
                              className="text-red-600 hover:text-red-800"
                              title={language === 'en' ? 'Delete Order' : 'Usuń zamówienie'}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </div>

                        {/* Order Items */}
                        <div className="space-y-2 mb-4">
                          {order.items.map((item, index) => (
                            <div key={`${item.id}-${index}`} className="flex justify-between items-center text-sm bg-gray-50 p-2 rounded">
                              <div className="flex items-center gap-2 flex-1">
                                <span className="text-gray-600 font-medium">{item.quantity}x</span>
                                <span className="text-gray-800">{item.name}</span>
                              </div>
                              <span className="font-medium text-gray-900">{(item.price * item.quantity).toFixed(2)} zł</span>
                            </div>
                          ))}
                        </div>

                        {/* Customer Info Summary - Always Visible */}
                        <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-3 rounded-lg mb-3">
                          <div className="flex items-center gap-2 text-sm">
                            <User className="w-4 h-4 text-indigo-600" />
                            <span className="font-medium text-gray-700">
                              {order.customer?.name || 'Guest'}
                            </span>
                            <span className="text-gray-400">•</span>
                            <span className="text-gray-600">{order.customer?.email || 'No email'}</span>
                            <span className="text-gray-400">•</span>
                            <span className="text-gray-600">{order.customer?.phone || 'No phone'}</span>
                          </div>
                        </div>

                        {/* Expanded Details */}
                        {showOrderDetails === order.id && (
                          <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-4">
                            {/* Product Details Section */}
                            <div>
                              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                <Package className="w-5 h-5 text-indigo-600" />
                                {language === 'en' ? 'Products' : 'Produkty'}
                              </h4>
                              <div className="space-y-3">
                                {order.items.map((item, index) => (
                                  <div key={`${item.id}-${index}`} className="bg-white p-3 rounded-lg border border-gray-200 flex gap-3">
                                    {/* Product Image */}
                                    {item.image ? (
                                      <img 
                                        src={item.image} 
                                        alt={item.name}
                                        className="w-20 h-20 object-cover rounded-lg border border-gray-300"
                                        onError={(e) => {
                                          (e.target as HTMLImageElement).src = 'https://via.placeholder.com/80?text=No+Image';
                                        }}
                                      />
                                    ) : (
                                      <div className="w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center">
                                        <Package className="w-8 h-8 text-gray-400" />
                                      </div>
                                    )}
                                    
                                    {/* Product Info */}
                                    <div className="flex-1 flex flex-col justify-between">
                                      <div>
                                        <div className="flex items-start justify-between gap-2">
                                          <h5 className="font-medium text-gray-900">{item.name}</h5>
                                          {item.slug && (
                                            <a
                                              href={`/product/${item.slug}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="text-indigo-600 hover:text-indigo-800 transition-colors flex-shrink-0"
                                              title={language === 'en' ? 'View product page' : 'Zobacz stronę produktu'}
                                            >
                                              <ExternalLink className="w-4 h-4" />
                                            </a>
                                          )}
                                        </div>
                                        <p className="text-xs text-gray-500 mt-1">ID: {item.id}</p>
                                        {/* Admin Note: show the note from the current product catalogue */}
                                        {(() => {
                                          const productNote = products.find(p => p.id === item.id)?.adminNote;
                                          return productNote ? (
                                            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2">
                                              <span className="font-semibold">
                                                {language === 'en' ? 'Note: ' : 'Notatka: '}
                                              </span>
                                              {productNote}
                                            </p>
                                          ) : null;
                                        })()}
                                      </div>
                                      <div className="flex items-center justify-between mt-2">
                                        <div className="text-sm text-gray-600">
                                          <span className="font-medium">{item.quantity}</span> × {item.price.toFixed(2)} zł
                                        </div>
                                        <div className="text-base font-bold text-indigo-900">
                                          {(item.price * item.quantity).toFixed(2)} zł
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>

                            {/* Customer Information Section */}
                            <div>
                              <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                                <User className="w-5 h-5 text-indigo-600" />
                                {language === 'en' ? 'Customer Information' : 'Informacje o kliencie'}
                              </h4>
                              {order.customer ? (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                  <div className="bg-white p-3 rounded border">
                                    <p className="text-gray-500 text-xs uppercase mb-1">{t.name[language]}</p>
                                    <p className="font-medium text-gray-900">{order.customer.name || 'N/A'}</p>
                                  </div>
                                  <div className="bg-white p-3 rounded border">
                                    <p className="text-gray-500 text-xs uppercase mb-1">{t.email[language]}</p>
                                    <p className="font-medium text-gray-900">{order.customer.email || 'N/A'}</p>
                                  </div>
                                  {order.customer.phone && (
                                    <div className="bg-white p-3 rounded border">
                                      <p className="text-gray-500 text-xs uppercase mb-1">{t.phone[language]}</p>
                                      <p className="font-medium text-gray-900">{order.customer.phone}</p>
                                    </div>
                                  )}
                                  <div className="bg-white p-3 rounded border md:col-span-2">
                                    <p className="text-gray-500 text-xs uppercase mb-1">{t.address[language]}</p>
                                    <p className="font-medium text-gray-900">
                                      {order.customer.address || 'N/A'}
                                      {order.customer.city && `, ${order.customer.city}`}
                                      {order.customer.zip && ` ${order.customer.zip}`}
                                    </p>
                                  </div>
                                  {order.customer.taxNumber && (
                                    <div className="bg-white p-3 rounded border md:col-span-2">
                                      <p className="text-gray-500 text-xs uppercase mb-1">
                                        {language === 'en' ? 'Tax ID / NIP' : 'NIP'}
                                      </p>
                                      <p className="font-medium text-gray-900">{order.customer.taxNumber}</p>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-500 italic">
                                  {language === 'en' ? 'No customer information available' : 'Brak informacji o kliencie'}
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Order Total */}
                        <div className="border-t border-gray-200 pt-4 mt-4 bg-gray-50 p-4 rounded-lg">
                          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Clock className="w-4 h-4" />
                              <span>{new Date(order.date).toLocaleDateString(language === 'en' ? 'en-US' : 'pl-PL', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}</span>
                            </div>
                            <div className="text-right w-full md:w-auto space-y-1">
                              <p className="text-sm text-gray-600">
                                {language === 'en' ? 'Net' : 'Netto'}: 
                                <span className="font-medium ml-2">{(Number(order.subtotal) || Number(order.total) / 1.23 || 0).toFixed(2)} zł</span>
                              </p>
                              <p className="text-sm text-gray-600">
                                VAT (23%): 
                                <span className="font-medium ml-2">{(Number(order.vat) || (Number(order.total) - Number(order.total) / 1.23) || 0).toFixed(2)} zł</span>
                              </p>
                              <div className="font-bold text-xl text-indigo-900 mt-2 pt-2 border-t border-gray-300">
                                {language === 'en' ? 'Total' : 'Razem'}: {(Number(order.total) || 0).toFixed(2)} zł
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* CATEGORIES TAB */}
          {activeTab === 'categories' && (
            <div className="space-y-6">
              {/* Add Category Button */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                {!showAddCategory ? (
                  <button
                    onClick={() => setShowAddCategory(true)}
                    className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-indigo-400 hover:text-indigo-600 transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                    {t.addCategory[language]}
                  </button>
                ) : (
                  <div className="space-y-4">
                    <h3 className="font-bold text-gray-800">{t.addCategory[language]}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <input
                        type="text"
                        placeholder="Category ID (e.g., sports)"
                        value={newCategory.id}
                        onChange={(e) => setNewCategory({ ...newCategory, id: e.target.value })}
                        className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                      />
                      <input
                        type="text"
                        placeholder={`${t.english[language]} ${t.categoryName[language]}`}
                        value={newCategory.nameEn}
                        onChange={(e) => setNewCategory({ ...newCategory, nameEn: e.target.value })}
                        className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                      />
                      <input
                        type="text"
                        placeholder={`${t.polish[language]} ${t.categoryName[language]}`}
                        value={newCategory.namePl}
                        onChange={(e) => setNewCategory({ ...newCategory, namePl: e.target.value })}
                        className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={handleAddCategory}
                        className="flex-1 bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 transition-colors"
                      >
                        {t.add[language]}
                      </button>
                      <button
                        onClick={() => {
                          setShowAddCategory(false);
                          setNewCategory({ id: '', nameEn: '', namePl: '' });
                        }}
                        className="px-6 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 transition-colors"
                      >
                        {t.cancel[language]}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Categories List */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-bold text-gray-800">{t.manageCategories[language]}</h2>
                  {categoriesLoading && (
                    <Loader2 className="w-5 h-5 animate-spin text-indigo-600" />
                  )}
                </div>
                <div className="grid gap-4">
                  {adminCategories.map(c => (
                    <div key={c.id} className="p-4 bg-gray-50 rounded-lg flex flex-col md:flex-row justify-between items-center gap-4">
                      {editingCategory?.id === c.id ? (
                        <div className="flex-1 w-full grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">{t.english[language]} Name</label>
                            <input 
                              type="text"
                              value={editingCategory.name.en}
                              onChange={(e) => setEditingCategory({
                                ...editingCategory,
                                name: { ...editingCategory.name, en: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-semibold text-gray-500 mb-1">{t.polish[language]} Name</label>
                            <input 
                              type="text"
                              value={editingCategory.name.pl}
                              onChange={(e) => setEditingCategory({
                                ...editingCategory,
                                name: { ...editingCategory.name, pl: e.target.value }
                              })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                            />
                          </div>
                        </div>
                      ) : (
                        <div className="flex-1 w-full text-center md:text-left">
                          <div className="flex flex-col md:flex-row md:items-baseline gap-1">
                            <span className="font-medium text-gray-900">{c.name['en']}</span>
                            <span className="hidden md:inline mx-2 text-gray-400">/</span>
                            <span className="font-medium text-gray-700">{c.name['pl']}</span>
                          </div>
                          <div className="text-xs text-gray-400 font-mono mt-1">ID: {c.id}</div>
                        </div>
                      )}
                      
                      <div className="flex gap-2 w-full md:w-auto justify-end">
                        {editingCategory?.id === c.id ? (
                          <>
                            <button 
                              onClick={handleUpdateCategory} 
                              className="p-2 bg-emerald-100 text-emerald-600 rounded-lg hover:bg-emerald-200 transition-colors"
                              title={t.save[language]}
                            >
                              <Save className="w-5 h-5" />
                            </button>
                            <button 
                              onClick={() => setEditingCategory(null)} 
                              className="p-2 bg-gray-200 text-gray-600 rounded-lg hover:bg-gray-300 transition-colors"
                              title={t.cancel[language]}
                            >
                              <X className="w-5 h-5" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button 
                              onClick={() => setEditingCategory(c)} 
                              className="p-2 bg-indigo-50 text-indigo-600 rounded-lg hover:bg-indigo-100 transition-colors"
                              title={t.edit[language]}
                            >
                              <Edit className="w-5 h-5" />
                            </button>
                            <button 
                              onClick={() => handleDeleteCategory(c.id)} 
                              className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                              title={t.delete[language]}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Category Pagination */}
              <Pagination
                currentPage={categoryPage}
                totalPages={categoryTotalPages}
                onPageChange={setCategoryPage}
              />
            </div>
          )}

          {/* LIBRARY TAB */}
          {activeTab === 'library' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold text-gray-900">
                  {language === 'en' ? 'Image Library' : 'Biblioteka Obrazów'}
                </h2>
              </div>

              {/* Add New Image */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold mb-4">
                  {language === 'en' ? 'Add New Image' : 'Dodaj nowy obraz'}
                </h3>
                
                {/* File Upload Section */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {language === 'en' ? 'Upload from Computer' : 'Prześlij z komputera'}
                  </label>
                  
                  {/* Drag and Drop Zone */}
                  <div
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-all ${
                      dragActive 
                        ? 'border-indigo-500 bg-indigo-50' 
                        : 'border-gray-300 hover:border-gray-400'
                    } ${uploadingFile ? 'opacity-50 pointer-events-none' : ''}`}
                  >
                    <input
                      type="file"
                      id="file-upload"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      onChange={handleFileInputChange}
                      disabled={uploadingFile}
                      multiple
                      className="hidden"
                    />
                    <label
                      htmlFor="file-upload"
                      className="cursor-pointer flex flex-col items-center"
                    >
                      <ImageIcon className={`w-12 h-12 mb-3 ${dragActive ? 'text-indigo-500' : 'text-gray-400'}`} />
                      <p className="text-sm font-medium text-gray-700 mb-1">
                        {uploadingFile
                          ? (language === 'en' ? 'Uploading...' : 'Przesyłanie...')
                          : dragActive
                          ? (language === 'en' ? 'Drop the images here' : 'Upuść obrazy tutaj')
                          : (language === 'en' ? 'Click to upload or drag and drop (multiple files)' : 'Kliknij, aby przesłać lub przeciągnij i upuść (wiele plików)')}
                      </p>
                      <p className="text-xs text-gray-500">
                        {language === 'en' 
                          ? 'PNG, JPG, GIF, WEBP up to 5MB each (max 200 files)' 
                          : 'PNG, JPG, GIF, WEBP do 5MB każdy (max 200 plików)'}
                      </p>
                    </label>
                  </div>
                </div>

                {/* Divider */}
                <div className="relative mb-6">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-300"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-2 bg-white text-gray-500">
                      {language === 'en' ? 'OR' : 'LUB'}
                    </span>
                  </div>
                </div>

                {/* URL Input Section */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {language === 'en' ? 'Add from URL' : 'Dodaj z URL'}
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="url"
                      value={newMediaUrl}
                      onChange={(e) => setNewMediaUrl(e.target.value)}
                      placeholder={language === 'en' ? 'Enter image URL' : 'Wprowadź adres URL obrazu'}
                      className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                    />
                    <button
                      onClick={handleAddMedia}
                      disabled={!newMediaUrl.trim()}
                      className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                    >
                      <Upload className="w-5 h-5" />
                      {language === 'en' ? 'Add' : 'Dodaj'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Media Grid */}
              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold">
                    {language === 'en' ? 'All Images' : 'Wszystkie obrazy'} ({mediaLibrary.length})
                  </h3>
                  <input
                    type="text"
                    value={mediaSearch}
                    onChange={(e) => setMediaSearch(e.target.value)}
                    placeholder={language === 'en' ? 'Search images...' : 'Szukaj obrazów...'}
                    className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none w-64"
                  />
                </div>

                {mediaLibrary.length === 0 ? (
                  <div className="text-center py-12 text-gray-500">
                    <ImageIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                    <p>{language === 'en' ? 'No images in library' : 'Brak obrazów w bibliotece'}</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {mediaLibrary
                      .filter(media => 
                        mediaSearch === '' || 
                        media.filename.toLowerCase().includes(mediaSearch.toLowerCase())
                      )
                      .map((media) => (
                        <div 
                          key={media.id}
                          className="group relative aspect-square rounded-lg overflow-hidden border-2 border-gray-200 hover:border-indigo-500 transition-all cursor-pointer"
                        >
                          <img 
                            src={media.url} 
                            alt={media.filename}
                            className="w-full h-full object-cover"
                          />
                          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-50 transition-all flex items-center justify-center gap-2">
                            <button
                              onClick={() => handleDeleteMedia(media.id)}
                              className="opacity-0 group-hover:opacity-100 transition-opacity p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                              title={t.delete[language]}
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2">
                            <p className="text-white text-xs truncate">{media.filename}</p>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Product Modal */}
      {showProductModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={closeProductModal}>
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-800">
                {editingProduct ? t.updateProduct[language] : t.addProduct[language]}
              </h2>
              <button 
                onClick={closeProductModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <form onSubmit={editingProduct ? handleUpdateProduct : handleAddProduct} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product Name *</label>
                  <input
                    value={editingProduct?.name || newProduct.name}
                    onChange={e => editingProduct 
                      ? setEditingProduct({...editingProduct, name: e.target.value})
                      : setNewProduct({...newProduct, name: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                    placeholder="e.g. Smart Watch"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.price[language]} *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingProduct?.price || newProduct.price}
                    onChange={e => editingProduct 
                      ? setEditingProduct({...editingProduct, price: parseFloat(e.target.value)})
                      : setNewProduct({...newProduct, price: parseFloat(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                    placeholder="0.00"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.categories[language]} *</label>
                  <select
                    value={editingProduct?.categoryId || newProduct.categoryId}
                    onChange={e => editingProduct 
                      ? setEditingProduct({...editingProduct, categoryId: e.target.value})
                      : setNewProduct({...newProduct, categoryId: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                  >
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name[language]}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.stock[language]}</label>
                  <input
                    type="number"
                    value={editingProduct?.stock || newProduct.stock}
                    onChange={e => editingProduct 
                      ? setEditingProduct({...editingProduct, stock: parseInt(e.target.value)})
                      : setNewProduct({...newProduct, stock: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.discount[language]} (%)</label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={editingProduct?.discount || newProduct.discount}
                    onChange={e => editingProduct 
                      ? setEditingProduct({...editingProduct, discount: parseInt(e.target.value)})
                      : setNewProduct({...newProduct, discount: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                    placeholder="0"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'en' ? 'Minimum Quantity' : 'Minimalna ilość'}
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={editingProduct?.minQuantity || newProduct.minQuantity || 6}
                    onChange={e => editingProduct 
                      ? setEditingProduct({...editingProduct, minQuantity: parseInt(e.target.value)})
                      : setNewProduct({...newProduct, minQuantity: parseInt(e.target.value)})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                    placeholder="6"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {language === 'en' 
                      ? 'The minimum quantity customers must order at a time' 
                      : 'Minimalna ilość, którą klienci muszą zamówić na raz'}
                  </p>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.imageUrl[language]}</label>
                  <div className="flex gap-2">
                    <input
                      value={editingProduct?.image || newProduct.image}
                      onChange={e => editingProduct 
                        ? setEditingProduct({...editingProduct, image: e.target.value})
                        : setNewProduct({...newProduct, image: e.target.value})}
                      className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none"
                      placeholder="https://example.com/image.jpg"
                    />
                    <button
                      type="button"
                      onClick={() => setShowImagePicker(true)}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
                    >
                      <ImageIcon className="w-4 h-4" />
                      {language === 'en' ? 'Browse Library' : 'Przeglądaj bibliotekę'}
                    </button>
                  </div>
                  {(editingProduct?.image || newProduct.image) && (
                    <img 
                      src={editingProduct?.image || newProduct.image} 
                      alt="Preview" 
                      className="mt-2 w-32 h-32 object-cover rounded-lg border"
                    />
                  )}
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t.productDetails[language]}</label>
                  <textarea
                    value={editingProduct?.description || newProduct.description}
                    onChange={e => editingProduct 
                      ? setEditingProduct({...editingProduct, description: e.target.value})
                      : setNewProduct({...newProduct, description: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none h-24"
                  />
                </div>

                {/* Admin Note — internal field, never shown to customers */}
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {language === 'en' ? 'Admin Note (internal)' : 'Notatka admina (wewnętrzna)'}
                  </label>
                  <textarea
                    value={editingProduct?.adminNote ?? newProduct.adminNote ?? ''}
                    onChange={e => {
                      // Security: enforce 500-char limit on the client side before storing
                      const val = e.target.value.slice(0, 500);
                      editingProduct
                        ? setEditingProduct({ ...editingProduct, adminNote: val })
                        : setNewProduct({ ...newProduct, adminNote: val });
                    }}
                    maxLength={500}
                    rows={3}
                    placeholder={language === 'en'
                      ? 'Internal note visible only in admin dashboard (max 500 chars)'
                      : 'Wewnętrzna notatka widoczna tylko w panelu admina (maks. 500 znaków)'}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-sm"
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {(editingProduct?.adminNote ?? newProduct.adminNote ?? '').length}/500
                  </p>
                </div>

                <div className="col-span-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editingProduct?.featured || newProduct.featured}
                      onChange={e => editingProduct 
                        ? setEditingProduct({...editingProduct, featured: e.target.checked})
                        : setNewProduct({...newProduct, featured: e.target.checked})}
                      className="w-4 h-4 text-indigo-600 rounded"
                    />
                    <span className="text-sm font-medium text-gray-700">{t.isFeatured[language]}</span>
                  </label>
                </div>

                <div className="col-span-2 flex gap-4 pt-4">
                  <button 
                    type="button"
                    onClick={closeProductModal}
                    className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-700 font-bold py-3 rounded-lg transition-colors"
                  >
                    {t.cancel[language]}
                  </button>
                  <button 
                    type="submit" 
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 rounded-lg transition-colors"
                  >
                    {editingProduct ? t.save[language] : t.add[language]}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Media Library (Image Picker) */}
      {showImagePicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={() => setShowImagePicker(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-800">
                {language === 'en' ? 'Select Image from Library' : 'Wybierz obraz z biblioteki'}
              </h2>
              <button 
                onClick={() => setShowImagePicker(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="p-6">
              {mediaLibrary.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <ImageIcon className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                  <p>{language === 'en' ? 'No images in library. Go to Library tab to add images.' : 'Brak obrazów w bibliotece. Przejdź do zakładki Biblioteka, aby dodać obrazy.'}</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {mediaLibrary.map((media) => (
                    <div 
                      key={media.id}
                      onClick={() => selectImageFromLibrary(media.url)}
                      className="group relative aspect-square rounded-lg overflow-hidden border-2 border-gray-200 hover:border-indigo-500 transition-all cursor-pointer"
                    >
                      <img 
                        src={media.url} 
                        alt={media.filename}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center">
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity p-2 bg-indigo-600 text-white rounded-lg">
                          {language === 'en' ? 'Select' : 'Wybierz'}
                        </div>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-2">
                        <p className="text-white text-xs truncate">{media.filename}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
